import React from "react";
import { LucideIcon } from "lucide-react";

// CSS Variables for consistent styling
export const pageLayoutStyles = {
  // Colors
  primary: {
    bg: "bg-blue-600",
    hover: "hover:bg-blue-700",
    text: "text-white",
    border: "border-blue-500",
    focus: "focus:ring-blue-500",
  },
  secondary: {
    bg: "bg-gray-100",
    hover: "hover:bg-gray-200",
    text: "text-gray-600",
    border: "border-gray-300",
    focus: "focus:ring-gray-500",
  },
  // Button styles
  button: {
    primary:
      "flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors",
    secondary:
      "flex items-center gap-2 bg-card border border-border text-content-text-muted px-4 py-2 rounded-lg hover:bg-sidebar-hover transition-colors",
    icon: "p-2 text-content-text-muted hover:text-primary transition-colors",
    iconDanger:
      "p-2 text-content-text-muted hover:text-destructive transition-colors",
  },
  // Layout styles
  layout: {
    container: "w-full px-4 sm:px-6 lg:px-8 py-8",
    header: "mb-8",
    headerContent: "flex items-center justify-between",
    headerLeft: "flex items-center gap-3",
    headerRight: "flex items-center gap-4",
    title: "text-3xl font-bold text-content-text",
    subtitle: "text-content-text-muted",
    icon: "w-8 h-8 text-primary",
  },
  // Filter styles
  filters: {
    container: "flex flex-col lg:flex-row gap-4 mb-6",
    search: "flex-1 relative",
    searchInput:
      "w-full pl-10 pr-4 py-2 bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent",
    searchIcon:
      "absolute left-3 top-1/2 transform -translate-y-1/2 text-input-placeholder w-4 h-4",
    filterButtons: "flex gap-2",
    filterButton:
      "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors",
    filterButtonActive: "bg-primary/10 border-primary text-primary",
    filterButtonInactive:
      "bg-card border-border text-content-text-muted hover:bg-sidebar-hover",
    select:
      "px-3 py-2 bg-input-bg border border-input-border text-input-text rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent",
  },
  // View switcher styles
  viewSwitcher: {
    container: "flex bg-sidebar-hover rounded-lg p-1",
    button: "p-2 rounded",
    buttonActive: "bg-card shadow",
    buttonInactive: "hover:bg-sidebar-hover",
  },
  // Table styles
  table: {
    container: "bg-card border border-border rounded-lg overflow-hidden",
    wrapper: "overflow-x-auto",
    table: "w-full",
    thead: "bg-sidebar-hover border-b border-border",
    th: "px-6 py-3 text-left text-xs font-medium text-content-text uppercase tracking-wider",
    tbody: "bg-card divide-y divide-border",
    tr: "hover:bg-sidebar-hover",
    td: "px-6 py-4 whitespace-nowrap",
    actionButton:
      "p-1 text-content-text-muted hover:text-primary transition-colors",
    actionButtonDanger:
      "p-1 text-content-text-muted hover:text-destructive transition-colors",
  },
  // Card styles
  card: {
    container:
      "bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow",
    content: "p-6",
    header: "flex items-start justify-between mb-4",
    title: "text-lg font-semibold text-content-text mb-2",
    description: "text-content-text-muted text-sm mb-4 line-clamp-2",
    footer: "flex items-center justify-between",
    meta: "flex items-center gap-2 text-sm text-content-text-muted",
    actions: "flex items-center gap-2",
  },
  // List styles
  list: {
    container: "space-y-4",
    item: "bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow",
    content: "flex items-center justify-between",
    left: "flex items-center gap-4",
    right: "flex items-center gap-4",
  },
  // Loading and error states
  states: {
    loading: "text-center py-12",
    loadingSpinner:
      "animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4",
    loadingText: "text-content-text-muted",
    error: "text-center py-12",
    errorIcon: "w-12 h-12 text-destructive mx-auto mb-4",
    errorTitle: "text-lg font-medium text-content-text mb-2",
    errorText: "text-content-text-muted",
    empty: "text-center py-12",
    emptyIcon: "w-12 h-12 text-content-text-muted mx-auto mb-4",
    emptyTitle: "text-lg font-medium text-content-text mb-2",
    emptyText: "text-content-text-muted mb-4",
  },
} as const;

// Reusable Page Header Component
interface PageHeaderProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  actionButton?: React.ReactNode;
  viewSwitcher?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  subtitle,
  actionButton,
  viewSwitcher,
}) => {
  return (
    <div className={pageLayoutStyles.layout.header}>
      <div className={pageLayoutStyles.layout.headerContent}>
        <div className={pageLayoutStyles.layout.headerLeft}>
          <Icon className={pageLayoutStyles.layout.icon} />
          <div>
            <h1 className={pageLayoutStyles.layout.title}>{title}</h1>
            <p className={pageLayoutStyles.layout.subtitle}>{subtitle}</p>
          </div>
        </div>
        <div className={pageLayoutStyles.layout.headerRight}>
          {viewSwitcher}
          {actionButton}
        </div>
      </div>
    </div>
  );
};

// Reusable Filter Section Component
interface FilterSectionProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  children?: React.ReactNode;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  children,
}) => {
  return (
    <div className={pageLayoutStyles.filters.container}>
      <div className={pageLayoutStyles.filters.search}>
        <Search className={pageLayoutStyles.filters.searchIcon} />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className={pageLayoutStyles.filters.searchInput}
        />
      </div>
      {children}
    </div>
  );
};

// Reusable View Switcher Component
interface ViewSwitcherProps {
  viewMode: "cards" | "list" | "table";
  onViewChange: (view: "cards" | "list" | "table") => void;
}

export const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
  viewMode,
  onViewChange,
}) => {
  return (
    <div className={pageLayoutStyles.viewSwitcher.container}>
      <button
        onClick={() => onViewChange("cards")}
        className={`${pageLayoutStyles.viewSwitcher.button} ${
          viewMode === "cards"
            ? pageLayoutStyles.viewSwitcher.buttonActive
            : pageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="Cards View"
      >
        <Grid3X3 className="w-4 h-4" />
      </button>
      <button
        onClick={() => onViewChange("list")}
        className={`${pageLayoutStyles.viewSwitcher.button} ${
          viewMode === "list"
            ? pageLayoutStyles.viewSwitcher.buttonActive
            : pageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="List View"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        onClick={() => onViewChange("table")}
        className={`${pageLayoutStyles.viewSwitcher.button} ${
          viewMode === "table"
            ? pageLayoutStyles.viewSwitcher.buttonActive
            : pageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="Table View"
      >
        <Table className="w-4 h-4" />
      </button>
    </div>
  );
};

// Reusable Action Button Component
interface ActionButtonProps {
  onClick: () => void;
  icon: LucideIcon;
  label: string;
  variant?: "primary" | "secondary";
  className?: string;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  icon: Icon,
  label,
  variant = "primary",
  className = "",
}) => {
  const baseClass =
    variant === "primary"
      ? pageLayoutStyles.button.primary
      : pageLayoutStyles.button.secondary;

  return (
    <button onClick={onClick} className={`${baseClass} ${className}`}>
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
};

// Reusable Icon Button Component
interface IconButtonProps {
  onClick: () => void;
  icon: LucideIcon;
  title: string;
  variant?: "default" | "danger";
  className?: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  onClick,
  icon: Icon,
  title,
  variant = "default",
  className = "",
}) => {
  const baseClass =
    variant === "danger"
      ? pageLayoutStyles.button.iconDanger
      : pageLayoutStyles.button.icon;

  return (
    <button
      onClick={onClick}
      className={`${baseClass} ${className}`}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

// Import required icons
import { Search, Grid3X3, List, Table } from "lucide-react";
